import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, inject } from '@angular/core';

import { Client } from 'src/providers/client/client';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { SuccessPage } from 'src/shared/pages/success/success';

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './go-live.page.html',
  styleUrls: ['./go-live.page.scss'],
  encapsulation: ViewEncapsulation.None
})
export class GoLivePage implements OnInit {
  private client = inject(Client);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  windowService = inject(WindowService);

  addressId: number | null = null;
  fromGettingStarted = false;
  isLive = false;
  isLoading = true;
  allStepsCompleted = false;

  private readonly STEP_IDS = [
    'onboarding-call', 'bank', 'locks', 'legal', 'pros',
    'direct', 'listing-opt', 'pricing', 'checklists',
    'smart-num', 'notify', 'listings'
  ];

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    this.addressId = params?.addressId
      || this.navCtrl.getParam('addressId')
      || Number(localStorage.getItem('dashboardAddressId'))
      || null;
    this.fromGettingStarted = !!params?.fromGettingStarted;
    this.rightSidePanelService.setDialogPageTitle('Go Live');
    await this.checkStatus();
  }

  private async checkStatus() {
    if (!this.addressId) {
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }
    try {
      const prefix = `external.onboarding_step.${this.addressId}.`;
      const keys = [
        ...this.STEP_IDS.map(id => `${prefix}${id}`),
        `${prefix}go_live`
      ];
      const metadata: any[] = await this.client.getMetadata(keys);
      const metaMap = new Map<string, string>();
      if (Array.isArray(metadata)) {
        for (const item of metadata) {
          if (item?.key) {
            const stripped = item.key.startsWith('external.') ? item.key.slice(9) : item.key;
            metaMap.set(stripped, item.value);
          }
        }
      }

      const completedCount = this.STEP_IDS.filter(
        id => metaMap.get(`onboarding_step.${this.addressId}.${id}`) === 'completed'
      ).length;
      this.allStepsCompleted = completedCount === this.STEP_IDS.length;

      if (metaMap.get(`onboarding_step.${this.addressId}.go_live`) === 'completed') {
        this.isLive = true;
      }
    } catch (err) {
      this.allStepsCompleted = true;
    }
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  handleGoLive = async () => {
    if (!this.addressId || this.isLive) {return;}

    if (!this.allStepsCompleted) {
      const successParams = {
        header: 'Please complete all the other onboarding steps before going live.',
        body: '',
        buttonText: 'Okay',
        buttonRoute: 'getting-started',
        allButtonsPrimary: true,
      };
      this.rightSidePanelService.setDialogPageTitle('Go Live');
      await this.rightSidePanelService.navigateTo('success', successParams, SuccessPage);
      return;
    }

    try {
      await this.client.addMetadata({
        key: `onboarding_step.${this.addressId}.go_live`,
        value: 'completed'
      });
      this.isLive = true;
      this.cdr.markForCheck();
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateBack('getting-started');
      }
    } catch (err) {
      this.util.showError('Failed to go live. Please try again.');
    }
  };
}
